import type { GeneratedMission } from '@bid/shared-types';

export interface MissionGenerationOptions {
  primaryFaction?: string;
  opposingFaction?: string;
  scoreType?: string;
  location?: string;
  customTarget?: string;
}

export const generateMission = (options: MissionGenerationOptions = {}): GeneratedMission => {
  // TODO: Implement actual mission generation logic
  return {
    target: options.customTarget || generateRandomTarget(),
    scoreType: options.scoreType || generateRandomScoreType(),
    complications: generateComplications(options.primaryFaction, options.opposingFaction),
    factionInvolvement: generateFactionInvolvement(options),
    rewards: generateRewards(),
    heatLevel: calculateHeatLevel(options),
    location: options.location || generateRandomLocation(),
  };
};

const generateRandomTarget = (): string => {
  const targets = [
    'A valuable artifact',
    'Secret documents',
    "A noble's heir",
    'A shipment of rare goods',
    'Bank vault',
    'Military supplies',
  ];
  return targets[Math.floor(Math.random() * targets.length)];
};

const generateRandomScoreType = (): string => {
  const types = ['Assault', 'Deception', 'Stealth', 'Social', 'Transport'];
  return types[Math.floor(Math.random() * types.length)];
};

const generateComplications = (_primaryFaction?: string, opposingFaction?: string): string[] => {
  const baseComplications = [
    'Guard patrols',
    'Locked doors',
    'Hidden traps',
    'Time pressure',
    'Unpredictable weather',
    'Hostile locals',
  ];

  const complications = [...baseComplications]
    .sort(() => 0.5 - Math.random())
    .slice(0, Math.floor(Math.random() * 2) + 2);

  if (opposingFaction) {
    complications.push(`${opposingFaction} agents are watching`);
  }

  return complications;
};

const generateFactionInvolvement = (options: MissionGenerationOptions): string[] => {
  const factions: string[] = [];

  // If location is neutral and no specific factions selected, return empty
  if (options.location === 'Neutral Ground' && !options.primaryFaction) {
    return factions;
  }

  if (options.primaryFaction) {
    factions.push(options.primaryFaction);
  }

  if (options.opposingFaction) {
    factions.push(options.opposingFaction);
  }

  // Only add random factions if location is not neutral and no factions specified
  if (factions.length === 0 && options.location !== 'Neutral Ground') {
    const randomFactions = ['The Lampblacks', 'The Red Sashes', 'The Gray Cloaks', 'The Billhooks'];
    factions.push(randomFactions[Math.floor(Math.random() * randomFactions.length)]);
  }

  return factions;
};

const generateRewards = (): { coin: number; other: string[] } => {
  return {
    coin: Math.floor(Math.random() * 4) + 4,
    other: ['Reputation', 'Faction status'],
  };
};

const calculateHeatLevel = (options: MissionGenerationOptions): number => {
  let baseHeat = 2;

  if (options.opposingFaction) {
    baseHeat += 2;
  }

  if (options.customTarget?.toLowerCase().includes('governor')) {
    baseHeat += 2;
  }

  return Math.min(baseHeat, 6);
};

const generateRandomLocation = (): string => {
  const locations = ["Crow's Foot", 'Charhollow', 'Six Towers', 'The Docks', 'Coalridge'];
  return locations[Math.floor(Math.random() * locations.length)];
};
